import { getMezzanineParent } from '@shared/helpers/design/storey.helpers';
import { isEqualPoints } from '@shared/helpers/geometry/geometry.helpers';
import type { Storey } from '@shared/models/design/storey.model';
import type { Point2D } from '@shared/models/geometry.model';

/**
 * A sub-range of one wall edge, expressed in normalized `[0..1]` space along
 * that edge, together with the line style it should be drawn with.
 */
export type SegmentRun = {
  startT: number;
  endT: number;
  isDashed: boolean;
};

/** A single straight wall piece between two consecutive shape corners. */
export type WallSegment = {
  start: Point2D;
  end: Point2D;
};

/**
 * Tolerance in normalized [0..1] edge space, used when comparing and merging
 * parametric overlap ranges. Unitless — one thousandth of an edge.
 */
const PARAMETRIC_EPSILON = 1e-3;

/**
 * How far a parent wall may sit from a mezzanine edge and still count as
 * running along that wall, in meters.
 *
 * Stored points drift by fractions of a millimeter through the metric to
 * lng/lat round trip applied on point insert and drag commit, so an exact
 * collinearity test dashes edges that visually follow the facade.
 */
const PARENT_WALL_DISTANCE_TOLERANCE_METERS = 0.02;

/** Shortest mezzanine edge that can carry a meaningful overlap test, in meters. */
const MIN_EDGE_LENGTH_METERS = 1e-3;

/**
 * Treat distance-to-edge as constant along a clipped parent wall when the
 * change in signed distance is at most this many meters.
 */
const DISTANCE_SPAN_EPSILON_METERS = 1e-3;

/**
 * Resolve draw runs for one wall edge of a storey outline.
 *
 * A mezzanine only has a real wall where it meets the exterior wall of its
 * parent storey. Everywhere else its outline is an open floor edge, which is
 * drawn dashed so the user can tell the two apart. Parts of the edge that run
 * along a parent wall are therefore solid, and the remainder — the parts facing
 * the open interior — are dashed.
 *
 * Non-mezzanine storeys have real walls all around and get a single solid run.
 *
 * @param isMezzanine Whether the current storey is a mezzanine
 * @param segmentIndex Index of the edge within `shapePoints`
 * @param shapePoints Open ring of the mezzanine shape (see {@link getOpenShapePoints})
 * @param parentWallSegments Parent storey walls from {@link getParentWallSegments}
 * @returns Ordered, non-empty sub-segments with dashed/solid style
 */
export function resolveMezzanineSegmentRuns({
  isMezzanine,
  segmentIndex,
  shapePoints,
  parentWallSegments,
}: {
  isMezzanine: boolean;
  segmentIndex: number;
  shapePoints: ReadonlyArray<Point2D>;
  parentWallSegments: ReadonlyArray<WallSegment>;
}): SegmentRun[] {
  const start = shapePoints[segmentIndex];
  const end = shapePoints[(segmentIndex + 1) % shapePoints.length];
  if (!start || !end) return [];
  if (!isMezzanine) return [{ startT: 0, endT: 1, isDashed: false }];

  const parentOverlapRanges = collectParentOverlapRanges(
    start,
    end,
    parentWallSegments,
  );
  if (!parentOverlapRanges.length)
    return [{ startT: 0, endT: 1, isDashed: true }];

  const merged = mergeRanges(parentOverlapRanges);
  const runs: SegmentRun[] = [];
  let cursor = 0;

  for (const range of merged) {
    const [overlapStart, overlapEnd] = range;
    if (overlapStart - cursor > PARAMETRIC_EPSILON) {
      // This part does not follow parent wall => dashed.
      runs.push({ startT: cursor, endT: overlapStart, isDashed: true });
    }

    // This part follows parent wall => solid.
    runs.push({ startT: overlapStart, endT: overlapEnd, isDashed: false });
    cursor = overlapEnd;
  }

  if (1 - cursor > PARAMETRIC_EPSILON) {
    // Tail part that does not follow parent wall => dashed.
    runs.push({ startT: cursor, endT: 1, isDashed: true });
  }

  return runs.filter((run) => run.endT - run.startT > PARAMETRIC_EPSILON);
}

/**
 * Build wall segments for the parent storey of a mezzanine.
 * @param storeys All storeys in the design
 * @param storey Current mezzanine storey
 * @returns Parent wall segments in {@link Point2D} space
 */
export function getParentWallSegments(
  storeys: Storey[],
  storey: Storey,
): WallSegment[] {
  const parent = getMezzanineParent(storeys, storey);
  if (!parent?.shapes?.length) return [];

  const segments: WallSegment[] = [];
  for (const shape of parent.shapes) {
    const points = getOpenShapePoints(shape.points);
    if (points.length < 2) continue;
    for (let i = 0; i < points.length; i += 1) {
      const start = points[i];
      const end = points[(i + 1) % points.length];
      if (!start || !end) continue;
      segments.push({ start, end });
    }
  }
  return segments;
}

/**
 * Collect normalized overlap ranges [tStart, tEnd] on segment AB for parent walls.
 * @param a Segment A start
 * @param b Segment A end
 * @param parentSegments Parent {@link WallSegment} list
 * @returns Overlap ranges along AB
 */
function collectParentOverlapRanges(
  a: Point2D,
  b: Point2D,
  parentSegments: ReadonlyArray<WallSegment>,
): [number, number][] {
  const ranges: [number, number][] = [];
  for (const segment of parentSegments) {
    const overlap = getParentOverlapRangeOnSegment(
      a,
      b,
      segment.start,
      segment.end,
    );
    if (!overlap) continue;
    ranges.push(overlap);
  }
  return ranges;
}

/**
 * Resolve the range of AB that runs along parent wall CD.
 *
 * CD is first clipped to the part that projects onto AB. Distance to AB varies
 * linearly along that clipped part, so the solid run is the sub-range where
 * |distance| stays within {@link PARENT_WALL_DISTANCE_TOLERANCE_METERS} —
 * not an all-or-nothing check on the clipped endpoints alone.
 * @param a Segment AB start
 * @param b Segment AB end
 * @param c Parent wall start
 * @param d Parent wall end
 * @returns [tStart, tEnd] on AB where the wall is followed
 */
function getParentOverlapRangeOnSegment(
  a: Point2D,
  b: Point2D,
  c: Point2D,
  d: Point2D,
): [number, number] | undefined {
  const abx = b[0] - a[0];
  const aby = b[1] - a[1];
  const lengthSq = abx * abx + aby * aby;
  if (lengthSq <= MIN_EDGE_LENGTH_METERS * MIN_EDGE_LENGTH_METERS)
    return undefined;
  const length = Math.sqrt(lengthSq);

  // Parent endpoints expressed as position along AB and signed distance to AB.
  // Both vary linearly along CD, so either can be interpolated.
  const tC = ((c[0] - a[0]) * abx + (c[1] - a[1]) * aby) / lengthSq;
  const tD = ((d[0] - a[0]) * abx + (d[1] - a[1]) * aby) / lengthSq;
  const distanceC = (abx * (c[1] - a[1]) - aby * (c[0] - a[0])) / length;
  const distanceD = (abx * (d[1] - a[1]) - aby * (d[0] - a[0])) / length;

  const from = Math.max(0, Math.min(tC, tD));
  const to = Math.min(1, Math.max(tC, tD));
  if (to - from <= PARAMETRIC_EPSILON) return undefined;

  // Non-degenerate: to - from > PARAMETRIC_EPSILON implies |tD - tC| is larger still.
  const span = tD - tC;
  const distanceFrom =
    distanceC + (distanceD - distanceC) * ((from - tC) / span);
  const distanceTo = distanceC + (distanceD - distanceC) * ((to - tC) / span);

  return clipProjectedRangeToDistanceTolerance(
    from,
    to,
    distanceFrom,
    distanceTo,
  );
}

/**
 * Restrict a projected [from, to] range to where |distance| ≤ tolerance.
 * Distance is treated as linear from {@link distanceFrom} at `from` to
 * {@link distanceTo} at `to`.
 * @returns Clipped [tStart, tEnd], or undefined when nothing stays in band
 */
function clipProjectedRangeToDistanceTolerance(
  from: number,
  to: number,
  distanceFrom: number,
  distanceTo: number,
): [number, number] | undefined {
  const spanT = to - from;
  if (spanT <= PARAMETRIC_EPSILON) return undefined;

  const dSpan = distanceTo - distanceFrom;
  const tolerance = PARENT_WALL_DISTANCE_TOLERANCE_METERS;

  // Constant (or near-constant) distance along the clipped wall.
  if (Math.abs(dSpan) <= DISTANCE_SPAN_EPSILON_METERS) {
    if (Math.abs(distanceFrom) > tolerance) return undefined;
    return [from, to];
  }

  // Solve -tol <= distanceFrom + u * dSpan <= tol for u in [0, 1].
  let uMin = 0;
  let uMax = 1;

  // distanceFrom + u * dSpan <= tol
  const upper = (tolerance - distanceFrom) / dSpan;
  if (dSpan > 0) uMax = Math.min(uMax, upper);
  else uMin = Math.max(uMin, upper);

  // distanceFrom + u * dSpan >= -tol
  const lower = (-tolerance - distanceFrom) / dSpan;
  if (dSpan > 0) uMin = Math.max(uMin, lower);
  else uMax = Math.min(uMax, lower);

  if (uMax - uMin <= PARAMETRIC_EPSILON) return undefined;
  return [from + uMin * spanT, from + uMax * spanT];
}

/**
 * Merge overlap ranges so fragmented parent segments become one solid run.
 * @param ranges Input ranges in normalized [0..1] space
 * @returns Merged ranges sorted by start
 */
function mergeRanges(
  ranges: ReadonlyArray<[number, number]>,
): [number, number][] {
  if (!ranges.length) return [];
  const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];

  for (const range of sorted) {
    const current = merged[merged.length - 1];
    if (!current) {
      merged.push([range[0], range[1]]);
      continue;
    }

    if (range[0] <= current[1] + PARAMETRIC_EPSILON) {
      current[1] = Math.max(current[1], range[1]);
      continue;
    }

    merged.push([range[0], range[1]]);
  }

  return merged;
}

/**
 * Interpolate a point on segment AB by normalized parameter t.
 * @param a Segment start
 * @param b Segment end
 * @param t Interpolation factor in [0..1]
 * @returns Interpolated point
 */
export function pointAtT(a: Point2D, b: Point2D, t: number): Point2D {
  const clampedT = Math.max(0, Math.min(1, t));
  return [a[0] + (b[0] - a[0]) * clampedT, a[1] + (b[1] - a[1]) * clampedT];
}

/**
 * Normalize shape points into an open ring (no duplicate closing point).
 * @param points Raw {@link Point2D} list
 * @returns Open list of points
 */
export function getOpenShapePoints(points: ReadonlyArray<Point2D>): Point2D[] {
  if (points.length < 2) return [...points];
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) return [...points];
  if (!isEqualPoints(first, last)) return [...points];
  return points.slice(0, -1);
}
